'use server'

import {
    Billing,
    BillingStatus,
    CreateBillingData,
    UpdateBillingData,
} from '../common/interfaces/billing.interface'
import { apiRequest, ApiResponse } from '../request'

const BASE = 'financial/billings'

export async function getBillings(params?: {
    page?: number
    limit?: number
    search?: string
    companyId?: number
    status?: string
    type?: string
    paymentMethod?: string
    clientId?: number
    partnerId?: number
    professionalId?: number
    /** Quem lançou a cobrança (nos atendimentos este mesmo campo é o vendedor). */
    createdById?: number
    startDate?: string
    endDate?: string
    /** Data usada pelo período: vencimento (padrão), pagamento ou criação. */
    dateField?: 'dueDate' | 'paidAt' | 'createdAt'
}): Promise<ApiResponse<Billing[]>> {
    return apiRequest<Billing[]>(BASE, { params: params || {} })
}

/** Totais de UM tipo (a receber ou a pagar) no recorte filtrado. Valores em centavos. */
export interface BillingSummaryTotals {
    count: number
    amount: number
    paidAmount: number
}

export interface BillingSummary {
    receivable: BillingSummaryTotals
    payable: BillingSummaryTotals
    count: number
}

/**
 * Totais do MESMO recorte da listagem — todas as páginas, não só a exibida.
 * Aceita os mesmos filtros de `getBillings` (menos paginação, que não se aplica).
 */
export async function getBillingsSummary(
    params?: Omit<Parameters<typeof getBillings>[0], 'page' | 'limit'>
): Promise<ApiResponse<BillingSummary>> {
    return apiRequest<BillingSummary>(`${BASE}/summary`, {
        params: params || {},
    })
}

export type QuoteMethod = 'PIX' | 'CREDIT_CARD' | 'DEBIT_CARD' | 'BOLETO'

/** Quanto o paciente paga numa forma de pagamento, com a taxa já embutida. */
export interface PaymentQuoteOption {
    method: QuoteMethod
    /** 1 = à vista. */
    installments: number
    /** Valor do serviço, sem taxa. */
    baseAmount: number
    /** Taxa embutida. Nunca exibida ao paciente — só o total é. */
    feeAmount: number
    /** O que o paciente paga, em centavos. */
    total: number
    /** Valor de cada parcela, em centavos. */
    installmentAmount: number
}

/**
 * Simula a cobrança sem criar nada: o total por forma de pagamento, já com a taxa
 * embutida, e as mesmas parcelas que o checkout vai oferecer.
 *
 * Existe para o operador não fechar um valor por telefone e a cobrança sair com
 * outro. O cálculo mora no backend de propósito — replicá-lo aqui faria a tela
 * divergir da cobrança na primeira mudança de regra.
 */
export async function getPaymentOptions(
    amount: number,
    methods?: QuoteMethod[]
): Promise<ApiResponse<{ baseAmount: number; options: PaymentQuoteOption[] }>> {
    return apiRequest(`${BASE}/payment-options`, {
        params: {
            amount,
            ...(methods?.length ? { methods: methods.join(',') } : {}),
        },
    })
}

/** Cobrança em aberto que aceita receber a soma de outro atendimento. */
export interface AttachableBilling {
    id: number
    /** Em CENTAVOS. */
    amount: number
    description: string | null
    dueDate: string
    paymentMethod: string | null
    createdAt: string
    /** Quantos atendimentos ela já cobre. */
    appointmentCount: number
}

/**
 * Cobranças do cliente onde dá para SOMAR o atendimento que está sendo agendado —
 * o paciente que marca exame em dois laboratórios e uma consulta paga uma vez só.
 *
 * Quem decide a elegibilidade é o backend (em aberto, sem pagamento gerado no
 * banco, fora de parcelamento, dos últimos 7 dias).
 */
export async function getAttachableBillings(
    clientId: number
): Promise<ApiResponse<AttachableBilling[]>> {
    return apiRequest<AttachableBilling[]>(`${BASE}/attachable`, {
        params: { clientId },
    })
}

export async function getBilling(id: number): Promise<ApiResponse<Billing>> {
    return apiRequest<Billing>(`${BASE}/${id}`, {})
}

// `deferGateway`: a tela vai parcelar a cobrança em seguida (parcelamento próprio).
// O backend não gera PIX/boleto nem envia o link do valor cheio — quem cobra é o
// `createInstallmentPlan`, já com o valor da entrada.
export async function createBilling(
    data: CreateBillingData,
    options?: { deferGateway?: boolean }
): Promise<ApiResponse<Billing>> {
    return apiRequest<Billing>(
        BASE,
        { data, params: options?.deferGateway ? { deferGateway: true } : {} },
        'post'
    )
}

export async function updateBilling(
    id: number,
    data: UpdateBillingData,
    options?: { deferGateway?: boolean }
): Promise<ApiResponse<Billing>> {
    return apiRequest<Billing>(
        `${BASE}/${id}`,
        { data, params: options?.deferGateway ? { deferGateway: true } : {} },
        'patch'
    )
}

// Parcelamento próprio: entrada + N parcelas com vencimentos escalonados.
export interface InstallmentPlanItem {
    amount: number // centavos
    dueDate: string // YYYY-MM-DD
    paymentMethod?: string
}
export interface CreateInstallmentPlanData {
    downPayment?: InstallmentPlanItem
    installments: InstallmentPlanItem[]
    paymentMethod?: string
}
export async function createInstallmentPlan(
    billingId: number,
    data: CreateInstallmentPlanData
): Promise<
    ApiResponse<{
        groupId: string
        entryBillingId: number
        billingIds: number[]
        total: number
    }>
> {
    return apiRequest(`${BASE}/${billingId}/installment-plan`, { data }, 'post')
}

export async function markBillingAsPaid(
    id: number,
    data?: { paidAmount?: number; paidAt?: string; orphanCheckNote?: string }
): Promise<ApiResponse<Billing>> {
    return apiRequest<Billing>(
        `${BASE}/${id}/mark-paid`,
        { data: data || {} },
        'patch'
    )
}

export async function cancelBilling(id: number): Promise<ApiResponse<Billing>> {
    return apiRequest<Billing>(`${BASE}/${id}/cancel`, {}, 'patch')
}

export interface RequestRefundResult {
    pending: true
    requestId: number
    message: string
}

/**
 * Abre uma solicitação de estorno na cobrança.
 *
 * Não devolve nada aqui: o estorno movimenta saldo para fora da conta digital e só sai
 * depois de aprovado com senha, na fila de estornos (/financial/bills-approvals). É o
 * mesmo fluxo disparado pelo cancelamento de um agendamento — mas nem toda cobrança nasce
 * de agendamento (pedido da loja, mensalidade, lançamento avulso), e essas também
 * precisam poder ser estornadas.
 *
 * `amount` em centavos; omitido, estorna todo o saldo ainda não devolvido.
 */
export async function requestBillingRefund(
    id: number,
    data: { amount?: number; reason: string }
): Promise<ApiResponse<RequestRefundResult>> {
    return apiRequest<RequestRefundResult>(
        `${BASE}/${id}/refund`,
        { data },
        'post'
    )
}

/** O que a consulta ao gateway encontrou. */
export type BillingSyncOutcome =
    | 'PAID'
    | 'ALREADY_PAID'
    | 'AWAITING_PAYMENT'
    | 'OVERDUE'
    | 'REFUNDED'
    | 'CANCELLED'
    | 'UNKNOWN'

export interface BillingSyncResult {
    outcome: BillingSyncOutcome
    changed: boolean
    status: BillingStatus
    providerStatus: string
    message: string
}

/**
 * Pergunta ao gateway o estado real da cobrança e reconcilia o que estiver defasado.
 *
 * Existe porque o webhook pode se perder: o cliente paga, o dinheiro entra na conta
 * digital, e a cobrança fica PENDENTE aqui. Chamado antes da baixa manual — se o
 * pagamento já entrou no gateway, a cobrança é baixada por ele mesmo (com repasse,
 * nota fiscal e confirmação); se não entrou, devolve `AWAITING_PAYMENT` e nada muda.
 *
 * Só faz sentido em cobrança gerada no gateway (`providerPaymentId` preenchido).
 */
export async function syncBillingWithProvider(
    id: number
): Promise<ApiResponse<BillingSyncResult>> {
    return apiRequest<BillingSyncResult>(
        `${BASE}/${id}/sync-provider`,
        {},
        'post'
    )
}

/**
 * O gateway em uso é o sandbox? Só então a tela mostra "Pagar (sandbox)". Quem trava
 * de verdade é o backend: fora do sandbox o pagamento é recusado com 403.
 */
export async function getSandboxPaymentAvailability(
    id: number
): Promise<ApiResponse<{ available: boolean }>> {
    return apiRequest<{ available: boolean }>(
        `${BASE}/${id}/sandbox-payment`,
        {},
        'get'
    )
}

/**
 * Paga o PIX da cobrança com o saldo de teste da subconta (só sandbox). A baixa não
 * vem desta resposta: chega pelo webhook, como em produção.
 */
export async function payBillingInSandbox(
    id: number
): Promise<ApiResponse<{ transactionId: string | null; status: string | null }>> {
    return apiRequest<{ transactionId: string | null; status: string | null }>(
        `${BASE}/${id}/sandbox-payment`,
        {},
        'post'
    )
}

export async function deleteBilling(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`${BASE}/${id}`, {}, 'delete')
}

export async function sendBillingPaymentEmail(
    id: number
): Promise<ApiResponse<{ sent: boolean }>> {
    return apiRequest<{ sent: boolean }>(`${BASE}/${id}/send-email`, {}, 'post')
}

/**
 * Gera (ou regera) os dados de pagamento no provider conforme o método da
 * cobrança (PIX/boleto). Se já havia dados, o backend cancela a cobrança antiga
 * no provider e emite uma nova — por isso a tela confirma antes quando já existem
 * dados. Devolve a cobrança atualizada, já com QR/linha digitável.
 */
export async function generateBillingPayment(
    id: number
): Promise<ApiResponse<Billing>> {
    return apiRequest<Billing>(`${BASE}/${id}/generate-payment`, {}, 'post')
}

/**
 * `receiptPendingApproval` indica que o comprovante anexado colocou a baixa na
 * fila de aprovação financeira. A leitura do comprovante pela IA acontece em
 * segundo plano (cron `receipt-review-tasks`), não no upload.
 */
export type BillingWithReceiptResult = Billing & {
    receiptPendingApproval?: boolean
}

export async function uploadBillingAttachment(
    id: number,
    file: File,
    description?: string
): Promise<ApiResponse<BillingWithReceiptResult>> {
    const formData = new FormData()
    formData.append('file', file)
    if (description) {
        formData.append('description', description)
    }
    return apiRequest<BillingWithReceiptResult>(
        `${BASE}/${id}/attachments`,
        { data: formData, formData: true },
        'post'
    )
}

export async function removeBillingAttachment(
    id: number,
    attachmentIndex: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(
        `${BASE}/${id}/attachments/${attachmentIndex}`,
        {},
        'delete'
    )
}

// Conciliação manual removida: a conciliação é automática (webhook BaaS +
// catch-up por extrato em digital-account). Os endpoints /reconcile e
// /reconciliation-pending não existem mais no backend.

/** Tipos de atendimento aos quais uma cobrança pode ser atrelada. */
export type LinkableAppointmentType = 'EXAM' | 'CONSULTATION' | 'PROCEDURE'

export interface LinkAppointmentResult {
    /** ANCHOR = era a primeira cobrança do atendimento; EXTRA = foi empilhada. */
    mode: 'ANCHOR' | 'EXTRA'
    appointmentType: LinkableAppointmentType
    appointmentId: number
    billingId: number
    message: string
}

/**
 * Vincula uma cobrança avulsa a um atendimento (cliente pagou por fora, via chave
 * PIX, e o agendamento já existia). Só vincula: se o atendimento não tem cobrança,
 * esta vira a principal; se já tem, esta é empilhada como pagamento adicional.
 * Nada é cancelado nem tem status alterado.
 *
 * Cobrança JÁ vinculada é TROCADA de atendimento: o backend desfaz o vínculo antigo
 * (cancelando o repasse dele) depois de validar o destino. Repasse já pago ou em lote
 * de aprovação recusa a troca.
 */
export async function linkBillingToAppointment(
    id: number,
    data: {
        appointmentType: LinkableAppointmentType
        appointmentId: number
    }
): Promise<ApiResponse<LinkAppointmentResult>> {
    return apiRequest<LinkAppointmentResult>(
        `${BASE}/${id}/link-appointment`,
        { data },
        'post'
    )
}

/**
 * Desfaz o vínculo da cobrança com o atendimento: ela volta a ser avulsa e o repasse
 * pendente é cancelado. Para atrelar a outro atendimento, use
 * `linkBillingToAppointment`, que já faz a troca sem passar por este estado solto.
 */
export async function unlinkBillingFromAppointment(
    id: number
): Promise<ApiResponse<{ billingId: number; message: string }>> {
    return apiRequest<{ billingId: number; message: string }>(
        `${BASE}/${id}/unlink-appointment`,
        {},
        'post'
    )
}

/** Uma forma de pagamento do split (parte do valor naquele meio). */
export interface BillingPaymentSplitLine {
    paymentMethod: string
    /** Centavos. */
    amount: number
}

/**
 * Refaz as formas de pagamento de uma cobrança existente. Redistribui o MESMO
 * total (a soma tem de bater com principal + filhas ativas). Recusa se já houver
 * pagamento gerado no gateway — nesse caso o caminho é cancelamento/estorno.
 */
export async function updateBillingPaymentSplits(
    id: number,
    paymentSplits: BillingPaymentSplitLine[],
    /** Pago em das formas recebidas por fora que a baixa automática quitar. */
    paidAt?: string
): Promise<
    ApiResponse<{
        principalId: number
        childrenCreated: number
        childrenCancelled: number
    }>
> {
    return apiRequest(
        `${BASE}/${id}/payment-splits`,
        { data: { paymentSplits, paidAt } },
        'put'
    )
}
